import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import List from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/List';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useMessagesContext } from 'Views/Page/Content/Templates/MessagesPage/MessagesContext';
import {
  FDBox, FDSecondaryActionIconButton, FDTitleText,
} from '@facet-wealth/design';
import {
  ThreadParticipant,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/MessagesPanel/components/MessagesPanelContent/components/ThreadHeader/components/ThreadParticipant/ThreadParticipant';
import { dataAttrs } from 'Helpers/Testing';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';

const ThreadParticipantList = ({
  searchedMembers,
  existingMembers,
  isSearching,
  onRemoveClick,
  handleClose,
}) => {
  const { displayErrorMessage } = useSnackbarContext();
  const { refreshAll } = useMessagesContext();
  const [assignToAgentLoading, setAssignToAgentLoading] = React.useState(false);

  const handleAssignToAgentClick = async (rel) => {
    // If disabled as this agent is already assigned, do nothing
    if (rel.meta('disabled')) {
      return;
    }

    try {
      setAssignToAgentLoading(true);
      await rel.link('related').post();
      await refreshAll();
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to assign thread to agent');
    } finally {
      setAssignToAgentLoading(false);
    }
  };

  const handleAddParticipant = member => async () => {
    if (!isSearching) {
      return;
    }

    try {
      await member.submit();
      await refreshAll();
      handleClose();
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  // Search members are the only ones that can be added right now.
  const memberArrayToMap = isSearching ? searchedMembers : existingMembers;
  const showNoUsersFound = memberArrayToMap.length === 0 && isSearching;

  return (
    <List>
      {showNoUsersFound ? (
        <FDBox paddingX={2}>
          <FDTitleText value={'No users found'} size={'sm'} color={'textDisabled'} />
        </FDBox>
      ) : null}
      {memberArrayToMap.map((member) => {
        const canRemove = member.meta().action('DELETE') && !isSearching;

        const assignElement = member.withRelationship(
          fr.relationships.assignToAgent.name,
          (rel) => {
            return (
              <FDSecondaryActionIconButton
                onClick={() => handleAssignToAgentClick(rel)}
                loading={assignToAgentLoading ?? false}
                icon={rel.meta('icon')}
                label={rel.meta('prompt')}
                {...dataAttrs('assign-to-agent-button')}
              />
            );
          },
          () => null,
        );
        return (
          <ThreadParticipant
            avatarSrc={member.relationship(fr.relationships.profileImage.name)?.link('related').url()}
            name={member.attr('name')}
            role={member.attr('role')}
            canRemove={canRemove}
            onRemove={(event) => onRemoveClick(member, event)}
            canAdd={member.attr('added') && !member.isTruthy('added')}
            onAdd={handleAddParticipant(member)}
            canAssign={assignElement !== null}
            assignElement={assignElement}
          />
        );
      })}
    </List>
  );
};

ThreadParticipantList.defaultProps = {
};

ThreadParticipantList.propTypes = {
  onRemoveClick: PropTypes.func.isRequired,
  searchedMembers: PropTypes.array.isRequired,
  existingMembers: PropTypes.array.isRequired,
  isSearching: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export { ThreadParticipantList };
